import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusSquare, PencilSimple, ChatCircleText, Copy, Trash, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/AppShell";
import { AgentAvatar } from "@/components/agent/AgentCard";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, errMsg, fmtPrice, fmtDate } from "@/lib/api";
import { useMeta } from "@/lib/useMeta";

export default function MyAgents() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: meta } = useMeta();
  const { data: agents = [], isLoading } = useQuery({ queryKey: ["agents"], queryFn: async () => (await api.get("/agents")).data });
  const label = (id) => meta?.models.find((m) => m.id === id)?.label || id;

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/agents/${id}/duplicate`);
      toast.success("Agente duplicado");
      navigate(`/agents/${data.agent_id}/edit`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/agents/${id}`);
      toast.success(data.archived ? "Agente archivado (los compradores conservan el acceso)" : "Agente eliminado");
      qc.invalidateQueries({ queryKey: ["agents"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const Btn = ({ onClick, to, icon: I, label: l, id }) => {
    const cls = "p-2 text-zinc-500 hover:bg-white/5 hover:text-white";
    return to ? <Link to={to} className={cls} title={l} data-testid={id}><I size={17} /></Link> : <button onClick={onClick} className={cls} title={l} data-testid={id}><I size={17} /></button>;
  };

  return (
    <div>
      <PageHeader eyebrow="Taller" title="Mis agentes" subtitle="Construye, prueba, versiona y publica." actions={<Link to="/agents/new" className="btn-primary" data-testid="agents-create-button"><PlusSquare size={18} /> Nuevo agente</Link>} />
      <div className="px-6 py-10 lg:px-12">
        {isLoading ? <div className="h-px bg-white/10 thinking-beam" /> : agents.length === 0 ? (
          <div className="border border-dashed border-white/15 p-16" data-testid="agents-empty">
            <p className="font-display text-3xl font-bold">Tu taller está vacío.</p>
            <p className="mt-2 max-w-md text-zinc-500">Crea tu primer agente desde una plantilla, con IA o desde cero.</p>
            <Link to="/agents/new" className="btn-primary mt-6" data-testid="agents-empty-create">Crear agente</Link>
          </div>
        ) : (
          <div className="border border-white/10" data-testid="agents-table">
            <div className="hidden grid-cols-[1fr_160px_120px_100px_110px_190px] gap-4 border-b border-white/10 px-5 py-3 text-[11px] font-semibold uppercase tracking-widest text-zinc-500 lg:grid">
              <span>Agente</span><span>Modelo</span><span>Estado</span><span>Precio</span><span>Actualizado</span><span className="text-right">Acciones</span>
            </div>
            {agents.map((a, i) => (
              <div key={a.agent_id} className="reveal grid grid-cols-1 items-center gap-4 border-b border-white/5 px-5 py-4 last:border-b-0 hover:bg-white/[0.02] lg:grid-cols-[1fr_160px_120px_100px_110px_190px]" style={{ animationDelay: `${i * 40}ms` }} data-testid={`agent-row-${a.agent_id}`}>
                <Link to={`/agents/${a.agent_id}/edit`} className="flex min-w-0 items-center gap-3">
                  <AgentAvatar icon={a.icon} color={a.color} size={40} />
                  <div className="min-w-0"><p className="truncate font-semibold">{a.name}</p><p className="truncate text-xs text-zinc-500">{a.category} · {a.skill_ids.length} skills · {a.doc_count || 0} docs</p></div>
                </Link>
                <span className="font-mono text-xs text-zinc-400">{label(a.model)}</span>
                <span className={`inline-flex w-fit items-center gap-2 border px-2 py-1 text-[11px] font-semibold uppercase tracking-wider ${a.published ? "border-emerald-500/30 text-emerald-400" : "border-white/10 text-zinc-500"}`}>
                  <span className={`h-1.5 w-1.5 ${a.published ? "bg-emerald-500" : "bg-zinc-600"}`} />{a.published ? "Publicado" : "Borrador"}
                </span>
                <span className="font-mono text-sm">{a.published ? fmtPrice(a) : "—"}</span>
                <span className="text-xs text-zinc-500">{fmtDate(a.updated_at)}</span>
                <div className="flex justify-start gap-1 lg:justify-end">
                  <Btn to={`/agents/${a.agent_id}/edit`} icon={PencilSimple} label="Editar" id={`agent-edit-${a.agent_id}`} />
                  <Btn to={`/chat/${a.agent_id}`} icon={ChatCircleText} label="Chat" id={`agent-chat-${a.agent_id}`} />
                  {a.published && <Btn to={`/agent/${a.agent_id}`} icon={Eye} label="Ver en marketplace" id={`agent-view-${a.agent_id}`} />}
                  <Btn onClick={() => duplicate(a.agent_id)} icon={Copy} label="Duplicar" id={`agent-duplicate-${a.agent_id}`} />
                  <AlertDialog>
                    <AlertDialogTrigger asChild><button className="p-2 text-zinc-500 hover:bg-red-500/10 hover:text-red-400" title="Eliminar" data-testid={`agent-delete-${a.agent_id}`}><Trash size={17} /></button></AlertDialogTrigger>
                    <AlertDialogContent className="rounded-sm border-white/10 bg-[#0f0f0f] text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar «{a.name}»?</AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">Se borrarán su configuración y entrenamiento. Si tiene compradores, se archivará y ellos conservarán el acceso.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm border-white/10 bg-transparent" data-testid="agent-delete-cancel">Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(a.agent_id)} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="agent-delete-confirm">Eliminar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
